import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  forwardRef,
  inject
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GlobalConstants } from 'src/app/base/global.constants';
import { FilteroptionsPipe } from 'src/app/pipes/filteroptions.pipe';
import { UtilService } from 'src/app/services/util.service';
import { debounce } from 'lodash-es';

@Component({
  selector: 'vb-default-val-comp',
  templateUrl: './default-val-comp.component.html',
  styleUrls: ['./default-val-comp.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DefaultValCompComponent),
      multi: true
    },
    FilteroptionsPipe
  ],
  standalone: false  //NOSONAR
})
export class DefaultValCompComponent implements OnInit, OnChanges, ControlValueAccessor {
  private util = inject(UtilService);
  filteroptionsPipe = inject(FilteroptionsPipe);

  @Input() formData: any = null;
  @Input() element: any = null;
  @Input() disableField = false;
  @Input() formGroup = null;
  dataType = '';
  defValue: any = null;
  private modelValue: any;
  dateOptions = [
    {
      label: 'Yesterday',
      value: 'yesterday'
    },
    {
      label: 'Today',
      value: 'today'
    },
    {
      label: 'Tomorrow',
      value: 'tomorrow'
    },
    {
      label: 'Start of Month',
      value: 'start_of_month'
    },
    {
      label: 'End of Month',
      value: 'end_of_month'
    }
  ];
  ynOptions = [
    {
      name: 'Yes',
      value: 'yes'
    },
    {
      name: 'No',
      value: 'no'
    }
  ];
  dynamicOptions: any[] = [];
  showDate = false;
  dateVal = '';
  onChange = (value: any) => {
    /* empty */
  };
  onTouched = () => {
    /* empty */
  };

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.modelValue = value;
    if (value && typeof value !== 'string' && (this.dataType === 'date' || this.dataType === 'datetime')) {
      this.showDate = true;
      this.defValue = value;
    } else if (value && typeof value === 'string' && (this.dataType === 'date' || this.dataType === 'datetime')) {
      if (!this.formData.timeOnly) {
        this.showDate = false;
      }
      this.dateVal = value;
    } else {
      this.dateVal = '';
      this.defValue = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    if (value === '') {
      value = null;
    }

    this.modelValue = value;
    try {
      this.onChange(this.modelValue);
    } catch (e) {
      console.error(e);
    }
  }

  onDateOptChange(event) {
    const val = event.value;
    if (val === 'select_a_date') {
      this.showDate = true;
    } else {
      this.showDate = false;
      this.set(event.value);
    }
  }

  debounceValChanges = debounce((value: any) => this.set(this.defValue), 1000);

  debounceDefValChanges = debounce(() => this.defValChanges(null), 500);

  defValChanges(event) {
    if (this.util.isvalidateDMProp(this.formData, this.dataType, this.defValue, 'Default Value')) {
      // For string data types, use debounce to prevent continuous calls
      // For other types (checkbox, boolean, etc.), call immediately
      if (this.dataType === 'string' || this.dataType === 'text') {
        this.debounceValChanges(this.defValue);
      } else {
        this.set(this.defValue);
      }
    }
  }

  defNumChanges(event) {
    if (this.util.isvalidateDMProp(this.formData, this.dataType, event.value, 'Default Value')) {
      this.debounceValChanges(event.value);
    }
  }

  getConfig() {
    this.dataType = this.formData?.f_type?.toLowerCase();
    if (this.formData?.timeOnly) {
      this.showDate = true;
      if (this.dateVal) {
        this.dateVal = '';
        this.set(null);
        this.writeValue(null);
      }
    } else if (this.dateVal !== 'select_a_date') {
      this.showDate = false;
    }
    if (this.formData?.sysGen) {
      this.disableField = true;
    }
    if (this.formData?.sysGen && this.formData?.name === GlobalConstants.isActiveField) {
      this.disableField = false;
    }
    if (this.dataType === 'dropdown' || this.dataType === 'radio' || this.dataType === 'checkboxgroup') {
      const options =
        this.filteroptionsPipe.transform(this.element?.possibleValuesFrom, this.formGroup, this.formData) || [];
      if (options) {
        this.dynamicOptions = options.map((op) => {
          return {
            label: op && op.name ? op.name : op,
            value: op && op.sid ? op.sid : op
          };
        });
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.formData) {
      if (
        (changes.formData?.currentValue?.f_minFrac || changes.formData?.previousValue?.f_minFrac) &&
        changes.formData?.currentValue?.f_minFrac !== changes.formData?.previousValue?.f_minFrac &&
        this.defValue
      ) {
        const minF = changes.formData?.currentValue?.f_minFrac;
        this.defValue = this.formatNumber(this.defValue, minF);
        this.defValChanges(changes.formData?.currentValue);
      }
    }
    this.getConfig();
  }

  ngOnInit(): void {
    this.getConfig();
  }

  formatNumber(value: any, minFractionDigits: number): string {
    value = Number(value);
    const minFormatted = value.toFixed(minFractionDigits);
    return minFormatted;
  }
}
